import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Heart, Info, Minus, Plus, XCircle } from 'lucide-react';
import { AGE_GROUPS, ANANDAMELA, AgeGroup, DAYS, DISH_DESCRIPTIONS_DE, DONATION_URL, MealMenu, MealType, emptyCounts, emptyDayMeals, eur, quote, toCents, validateCustomer, validateSelection } from 'types';
import ReservationCart from '../components/ReservationCart';
import { donationOf, useBooking } from '../state/BookingContext';

const MAX = 50;
const AGE_EMOJI: Record<AgeGroup, string> = { adult: '🧑', kid: '🧒', infant: '👶' };

function Stepper({ value, label, onChange, compact }: { value: number; label: string; onChange: (delta: number) => void; compact?: boolean }) {
  const btn = compact ? 'w-6 h-6' : 'w-8 h-8';
  const icon = compact ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <div className={`flex items-center ${compact ? 'space-x-2' : 'space-x-3'}`}>
      <button type="button" aria-label={`Remove one ${label}`} onClick={() => onChange(-1)} disabled={value === 0}
        className={`${btn} rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center`}><Minus className={icon} /></button>
      <span className={`${compact ? 'w-5 text-sm' : 'w-8'} text-center font-semibold`} aria-label={`${label} count`}>{value}</span>
      <button type="button" aria-label={`Add one ${label}`} onClick={() => onChange(1)} disabled={value >= MAX}
        className={`${btn} rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 disabled:opacity-40 flex items-center justify-center`}><Plus className={icon} /></button>
    </div>
  );
}

/** One veg or non-veg thali for a day: its dish list, and - deliberately secondary, off to the side rather than
 * taking over the card - price and seat counts. When soldOut, neither price nor the counters are shown at all -
 * any already-selected count can still be cleared via the cart's remove button. */
function MealCard({ type, menu, day, counts, onChange, soldOut }: { type: MealType; menu: MealMenu; day: string; counts: Record<AgeGroup, number>; onChange: (age: AgeGroup, delta: number) => void; soldOut?: boolean }) {
  const veg = type === 'veg';
  return (
    <div className={`flex flex-col sm:flex-row gap-4 bg-white rounded-2xl border shadow-sm p-4 ${soldOut ? 'border-gray-200 opacity-75' : 'border-rose-100'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span aria-hidden="true" className={`w-2 h-2 rounded-full flex-none ${veg ? 'bg-green-500' : 'bg-red-500'}`} />
          <h3 className="font-semibold text-rose-900">{day} {veg ? 'Veg' : 'Non-Veg'}</h3>
        </div>
        <ul className="space-y-2">
          {menu.items.map((item) => (
            <li key={item} className="leading-snug">
              <span className="text-sm font-medium text-rose-900">{item}</span>
              {DISH_DESCRIPTIONS_DE[item] && <span className="block text-xs text-gray-500 mt-0.5">{DISH_DESCRIPTIONS_DE[item]}</span>}
            </li>
          ))}
        </ul>
      </div>

      {!soldOut && (
        <div className="flex-none sm:w-56 space-y-1.5 border-t sm:border-t-0 sm:border-l border-rose-50 pt-3 sm:pt-0 sm:pl-4">
          {AGE_GROUPS.map((age) => (
            <div key={age.id} className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm text-gray-700">
                {age.name}s ( <span className="font-semibold text-rose-900">{menu.prices[age.id] > 0 ? eur(toCents(menu.prices[age.id])) : 'Free'}</span> )
              </span>
              <Stepper compact value={counts[age.id]} label={`${day} ${veg ? 'vegetarian' : 'non-vegetarian'} ${age.name.toLowerCase()}`} onChange={(d) => onChange(age.id, d)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Card = ({ title, subtitle, highlight, children }: { title: string; subtitle?: string; highlight?: boolean; children: React.ReactNode }) => (
  <section className={`bg-white rounded-xl shadow-sm border overflow-hidden ${highlight ? 'border-orange-400 ring-2 ring-orange-200' : 'border-gray-100'}`}>
    <div className="px-4 py-3 bg-orange-50 border-b"><h2 className="font-semibold text-gray-800">{title}</h2>{subtitle && <p className="text-xs text-gray-600">{subtitle}</p>}</div>
    {children}
  </section>
);

export default function BookingPage() {
  const navigate = useNavigate();
  const { draft, update } = useBooking();
  const { selection, customer, donate, donationText } = draft;
  const [showErrors, setShowErrors] = useState(false);

  const donation = donationOf(draft);
  const bump = (date: string | null, type: MealType | null, age: AgeGroup, delta: number) => {
    const next = JSON.parse(JSON.stringify(selection));
    const counts = date && type ? ((next.meals[date] ??= emptyDayMeals())[type]) : next.anandamela;
    counts[age] = Math.min(MAX, Math.max(0, (counts[age] ?? 0) + delta));
    update({ selection: next });
  };
  const removeMeal = (date: string, type: MealType) => {
    const next = JSON.parse(JSON.stringify(selection));
    (next.meals[date] ??= emptyDayMeals())[type] = emptyCounts();
    update({ selection: next });
  };
  const removeAnandamela = () => {
    update({ selection: { ...selection, anandamela: emptyCounts() } });
  };

  // Errors are shown once the user tries to continue.
  const selectionError = validateSelection({ ...selection, donation: 0 });
  const nameError = validateCustomer({ ...customer, email: 'x@y.zz' });
  const emailError = validateCustomer({ ...customer, name: 'xx' });
  const donationError = donate && !(donation >= 1 && donation <= 10000) ? 'Please enter a donation between €1 and €10000, or untick the donation.' : null;
  const firstError = selectionError ?? nameError ?? emailError ?? donationError;

  const q = quote({ ...selection, donation: donation >= 1 ? donation : 0 });
  // Jump to (and focus) the first thing that still needs filling in.
  const goTo = (id: string) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  };
  const next = () => {
    setShowErrors(true);
    if (firstError) return goTo(selectionError ? 'meals' : nameError ? 'name' : emailError ? 'email' : 'donation');
    navigate('/review');
  };
  // Meals chosen but details missing: nudge, without shouting before the user has picked anything.
  const detailsMissing = !selectionError && (nameError || emailError || donationError);
  const err = (msg: string | null) => showErrors && msg && <p className="text-xs text-red-600 mt-1">{msg}</p>;
  const field = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 text-sm';

  const allSoldOut = DAYS.every((d) => d.soldOut) && ANANDAMELA.soldOut;

  return (
    <div className="grid lg:grid-cols-3 gap-6 items-start">
      <div className={allSoldOut ? 'lg:col-span-3 space-y-6' : 'lg:col-span-2 space-y-6'}>
        {allSoldOut && (
          <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-br from-amber-50 to-rose-50 border-2 border-amber-200 rounded-2xl">
            <Heart className="w-6 h-6 text-rose-600 fill-rose-200 flex-none" aria-hidden="true" />
            <div>
              <h2 className="text-lg font-bold text-rose-900">Registrations are now closed</h2>
              <p className="text-sm text-gray-700">Our hearts are full - thank you for the incredible response! Every day of Durga Puja 2026 is now fully booked.</p>
            </div>
          </div>
        )}

        <p id="meals" tabIndex={-1} className="text-sm text-gray-700">Choose how many people will have meals on each day. Infants food is not charged. Prices are in euros.</p>

        <div className="flex gap-2 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-900">
          <Info className="w-4 h-4 mt-0.5 flex-none" aria-hidden="true" />
          <ul className="list-disc list-inside space-y-1">
            <li>This covers dinner registration only.</li>
            <li>Snacks and beverages will be available for purchase separately at the venue.</li>
            <li>Please complete your dinner registration now - once registrations close, we won't be able to accommodate further requests, as we'll need exact numbers for our vendors.</li>
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 bg-rose-50 border border-rose-100 rounded-xl">
          <span className="text-xs font-semibold text-rose-800 uppercase tracking-wide">Age group legend</span>
          {AGE_GROUPS.map((age) => (
            <span key={age.id} className="flex items-center gap-1.5 text-sm text-rose-900">
              <span aria-hidden="true" className="text-base">{AGE_EMOJI[age.id]}</span>
              <span className="font-medium">{age.name}s</span>
              <span className="text-rose-400 text-xs">({age.range})</span>
            </span>
          ))}
        </div>

        {DAYS.map((day, i) => (
          <div key={day.date}>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-5 h-5 text-amber-600 flex-none" aria-hidden="true" />
              <div>
                <h2 className="text-2xl font-serif font-bold text-rose-900 leading-tight">{day.name}</h2>
                <p className="text-xs text-rose-400 font-medium">Day {i + 1} · {day.dateLabel}</p>
              </div>
            </div>
            <div className="h-0.5 bg-gradient-to-r from-amber-400 via-rose-300 to-transparent rounded-full mb-4" />
            {day.soldOut && (
              <div className="flex gap-2 px-4 py-3 mb-4 bg-red-50 border-2 border-red-300 rounded-xl text-sm text-red-800">
                <XCircle className="w-4 h-4 mt-0.5 flex-none text-red-600" aria-hidden="true" />
                <p>We're grateful for the overwhelming response, but {day.name} is currently booked out - we're no longer able to take any further food registrations for this day.</p>
              </div>
            )}
            <div className="space-y-4">
              {(['veg', 'nonveg'] as MealType[]).map((type) => day.menus[type] && (
                <MealCard key={type} type={type} menu={day.menus[type]!} day={day.name} soldOut={day.soldOut} counts={{ infant: 0, kid: 0, adult: 0, ...selection.meals[day.date]?.[type] }} onChange={(age, d) => bump(day.date, type, age, d)} />
              ))}
            </div>
          </div>
        ))}

        <Card title={`Day 5 · ${ANANDAMELA.name}`} subtitle={ANANDAMELA.dateLabel}>
          <div className="p-4">
            {ANANDAMELA.soldOut && (
              <div className="flex gap-2 px-4 py-3 mb-3 bg-red-50 border-2 border-red-300 rounded-xl text-sm text-red-800">
                <XCircle className="w-4 h-4 mt-0.5 flex-none text-red-600" aria-hidden="true" />
                <p>We're grateful for the overwhelming response, but {ANANDAMELA.name} is currently booked out - we're no longer able to take any further registrations.</p>
              </div>
            )}
            <p className="text-xs text-gray-600 mb-3">{ANANDAMELA.description}</p>
            {!ANANDAMELA.soldOut && (
              <div className="space-y-2">
                {AGE_GROUPS.map((age) => {
                  const count = selection.anandamela[age.id] ?? 0;
                  const price = ANANDAMELA.prices[age.id];
                  return (
                    <div key={age.id} className={`flex items-center justify-between p-2 rounded-lg ${count > 0 ? 'bg-orange-50' : 'bg-gray-50'}`}>
                      <div>
                        <span className="text-sm font-medium">{age.name}</span> <span className="text-xs text-gray-500">({age.range})</span>
                        <span className={`block text-xs font-semibold ${price > 0 ? 'text-orange-600' : 'text-green-600'}`}>{price > 0 ? eur(toCents(price)) : 'Free'}</span>
                      </div>
                      <Stepper value={count} label={`Anandamela ${age.name.toLowerCase()}`} onChange={(d) => bump(null, null, age.id, d)} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        <section className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1.5">
            <Heart className="w-6 h-6 text-rose-600 fill-rose-200 flex-none" aria-hidden="true" />
            <h2 className="text-lg font-bold text-rose-900">Support Durga Puja 2026</h2>
          </div>
          {allSoldOut ? (
            <p className="text-sm text-gray-700">
              Sanskriti e.V. is a non-profit, run entirely by volunteers. Reservations are closed, but you can still donate to us here:{' '}
              <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-rose-800 underline hover:text-rose-900">{DONATION_URL}</a>
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-700 mb-4">
                Sanskriti e.V. is a non-profit, run entirely by volunteers. A donation - big or small - directly helps us bring this celebration to the community. Thank you for considering it!
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[10, 25, 50, 100].map((amount) => {
                  const active = donate && donationText.trim() === String(amount);
                  return (
                    <button key={amount} type="button" onClick={() => update({ donate: true, donationText: String(amount) })}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${active ? 'bg-rose-700 border-rose-700 text-white' : 'bg-white border-rose-200 text-rose-800 hover:border-rose-400'}`}>
                      €{amount}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-36">
                  <label htmlFor="donation" className="sr-only">Other donation amount (EUR)</label>
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
                  <input id="donation" inputMode="decimal" placeholder="Other amount" className={`${field} pl-7`} value={donationText}
                    onChange={(e) => update({ donate: true, donationText: e.target.value })} />
                </div>
                {donate && (
                  <button type="button" onClick={() => update({ donate: false })} className="text-xs text-gray-500 underline hover:text-gray-700">
                    No thanks, not this time
                  </button>
                )}
              </div>
              {err(donationError)}
            </>
          )}
        </section>

        {!allSoldOut && (
          <Card title="Your details" subtitle={detailsMissing ? 'Required to continue' : undefined} highlight={!!detailsMissing}>
            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                <input id="name" className={field} value={customer.name} autoComplete="name" onChange={(e) => update({ customer: { ...customer, name: e.target.value } })} />
                {err(nameError)}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                <input id="email" type="email" className={field} value={customer.email} autoComplete="email" onChange={(e) => update({ customer: { ...customer, email: e.target.value } })} />
                <p className="text-xs text-gray-500 mt-1">Your invoice will be sent here.</p>
                {err(emailError)}
              </div>
            </div>
          </Card>
        )}
      </div>

      {!allSoldOut && (
        <div className="lg:sticky lg:top-4">
          <ReservationCart quote={q} onRemoveMeal={removeMeal} onRemoveAnandamela={removeAnandamela}>
            {showErrors && selectionError ? (
              <p role="alert" className="text-sm text-red-600 mt-3">{selectionError}</p>
            ) : detailsMissing && (
              <button type="button" onClick={() => goTo(nameError ? 'name' : emailError ? 'email' : 'donation')}
                className={`mt-3 w-full text-left text-sm rounded-lg p-3 border ${showErrors ? 'text-red-700 bg-red-50 border-red-200' : 'text-orange-800 bg-orange-50 border-orange-200'}`}>
                Next: {nameError || emailError ? <>enter your {nameError ? 'name' : 'email address'} in <span className="underline font-semibold">Your details</span></> : <>fix your <span className="underline font-semibold">donation amount</span></>} ↓
              </button>
            )}
            <button onClick={next} className="mt-4 w-full bg-rose-800 text-white py-3 rounded-lg font-semibold hover:bg-rose-900">Proceed to Checkout →</button>
          </ReservationCart>
        </div>
      )}
    </div>
  );
}
